import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Box, Stack, TextField, Select, MenuItem, FormHelperText } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm, Controller, useWatch } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup'
import { omit } from 'lodash-es';
import { collection, onSnapshot, query } from 'firebase/firestore';

import AlertsContext from '../AlertsContext';
import TicketsList from './TicketsList';
import { CartContext } from '../CartContext';
import CollectPaymentDialog from './CollectPaymentDialog';
import { db } from '../firebase';
import TimeSlotSelector from './TimeSlots';
import { getCalendarOverwrites, parseDateAndTime } from '../helpers';
import { defaultEndTime, defaultSlotSize, defaultStartTime } from 'shared';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { timeSlotPurchases, usePurchases } from '../screens/admin/Schedule';

const schema = yup.object({
  ticketLocation: yup.string().required("Select the location").label('Location'),
  email: yup.string().email().label('Email'),
}).required();

const locationCollection = collection(db, 'locations');

// Intend to use this component in TicketPage and Terminal
const Checkout = ({ terminal, seletedReader }) => {
  const { setAlert } = useContext(AlertsContext);
  const { items, clearCart } = useContext(CartContext);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState();
  const [openCollectPaymentDailog, setOpenCollectionPaymentDailog] = useState(false);
  const [selectedTime, setSelectedTime] = useState();
  const [overwrites, setOverwrites] = useState([]);
  const dayOfWeek = format(new Date(), 'EEEE').toLowerCase();

  const { control, watch, setValue, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      ticketLocation: 'sydney',
      coupon: '',
      firstName: '',
      lastName: '',
      mobile: '',
      email: '',
      postcode: '',
    },
    resolver: yupResolver(schema)
  });

  const handleCollectPayment = async (stripeOptions) => {
    if (Number(process.env.REACT_APP_TERMINAL_SIMULATE) === 1)
      terminal.setSimulatorConfiguration({ testCardNumber: '4000050360000001' });

    const result = await terminal.collectPaymentMethod(stripeOptions.clientSecret)

    if (result.error) {
      console.log(result.error)
      setAlert({
        type: "error",
        message: result.error.message
      });
    } else {
      const processResult = await terminal.processPayment(result.paymentIntent);
      if (processResult.error) {
        // Placeholder for handling result.error
        console.log(processResult.error)
        setAlert({
          type: "error",
          message: processResult.error.message
        });
      } else if (processResult.paymentIntent) {
        // paymentIntentId = result.paymentIntent.id;
        // console.log('processPayment', processResult.paymentIntent);
        const response = await fetch(process.env.REACT_APP_FN_URL + '/checkout', {
          method: "POST",
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ paymentIntentId: processResult.paymentIntent.id })
        }).then(res => res.json())

        if (response.status === 'success') {
          reset();
          clearCart();
          setTotalAmount();
          setAlert("Success.");
        } else {
          setAlert({
            type: "error",
            message: "Oops, something went wrong. Please contact us."
          });
        }
      }
    }
  };

  const initPayment = async (data) => {
    try {
      const now = dayjs();
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discountCode: data?.coupon,
          ticketLocation: data?.ticketLocation,
          startDate: now.toDate(),
          selectedDateTime: parseDateAndTime(now, selectedTime),
          tickets: items.map(item => omit(item, ["discountAmount", "title"])),
          isTerminal: true,
          user: {
            firstName: data?.firstName,
            lastName: data?.lastName,
            mobile: data?.mobile,
            email: data?.email,
            postcode: data?.postcode,
          }
        })
      }

      const res = await fetch(process.env.REACT_APP_FN_URL + "/initPayment", requestOptions)
      const result = res.json();
      return result;
    } catch (error) {
      console.log(error);
      setAlert({
        type: 'error',
        message: "Oops, something went wrong. Please contact us."
      })
    }
  }

  const onSubmit = async data => {
    setIsLoading(true);
    if (items && items.length === 0) {
      setAlert({
        type: "error",
        message: "Cart is empty. You should add one more ticket."
      });
      setIsLoading(false);
      return;
    }
    setOpenCollectionPaymentDailog(true);
    const stripeOptions = await initPayment(data);
    setTotalAmount(stripeOptions.totalAmount);
    await handleCollectPayment(stripeOptions);
    setIsLoading(false);
  }

  const handleCancelPayment = () => {
    terminal.cancelCollectPaymentMethod();
    setTotalAmount();
    setOpenCollectionPaymentDailog(false);
    setIsLoading(false);
    reset();
    clearCart();
  }

  useEffect(() => {
    const unsubscribe = onSnapshot(query(locationCollection), (querySnapshot) => {
      const list = [];
      querySnapshot.forEach((doc) => {
        list.push({ ...doc.data(), id: doc.id });
      });
      setLocations(list);
    });
    return () => unsubscribe();
  }, []);


  const ticketLocation = watch('ticketLocation');
  const purchases = usePurchases({location: ticketLocation});

  const getSlotInfo = useCallback((time) => {
    const slotPurchases = timeSlotPurchases(purchases, time);
    return {
      people: slotPurchases.reduce((acc, curr) => acc + curr.groupSize, 0),
      groups: slotPurchases.length,
    }
  }, [purchases]);

  useEffect(() => {
    getCalendarOverwrites(ticketLocation, true).then(setOverwrites);
  }, [ticketLocation]);


  const ticketLocationInfo = locations.find((location) => location.id === ticketLocation);
  const customHours = overwrites.find(d => d.date === dayjs(new Date())?.format('YYYY-MM-DD') && d.time);

  return (
    <Box sx={{ marginTop: 4, marginBottom: 8 }}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2}>
          <Controller
            name="ticketLocation"
            control={control}
            render={({ field }) => <TextField select
              id="ticketLocation"
              className='ticketLocation'
              label="Location"
              {...field}
              onChange={(e) => {
                clearCart();
                setValue('ticketLocation', e.target.value);
              }}
            >
              {
                locations.map((location) => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)
              }
            </TextField>}
          />
          {
            errors?.ticketLocation && (
              <FormHelperText error>{errors.ticketLocation?.message}</FormHelperText>
            )
          }
          <TicketsList ticketLocation={ticketLocation} />
          <TimeSlotSelector
            startTime={customHours?.time?.from || ticketLocationInfo?.hours?.[dayOfWeek]?.from || defaultStartTime} 
            endTime={customHours?.time?.to || ticketLocationInfo?.hours?.[dayOfWeek]?.to || defaultEndTime} 
            slotSize={defaultSlotSize} 
            selectedTime={selectedTime} 
            setSelectedTime={setSelectedTime} 
            selectedDate={dayjs()}
            disabledTimes={[]}
            slotInfo={getSlotInfo}
          />
          <Controller
            name="coupon"
            control={control}
            render={({ field }) => <TextField
              className='coupon'
              label="Coupon or Voucher"
              autoFocus
              {...field}
            />}
          />
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => <TextField
              name="firstName"
              fullWidth
              id="firstName"
              label="First Name"
              autoFocus
              {...field}
            />}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => <TextField
              name="lastName"
              fullWidth
              id="lastName"
              label="Last Name"
              autoFocus
              {...field}
            />}
          />
          <Controller
            name="mobile"
            control={control}
            render={({ field }) => <TextField
              fullWidth
              id="phone"
              type="tel"
              label="Mobile phone"
              name="phone"
              autoComplete="phone"
              {...field}
            />}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => <TextField
              fullWidth
              id="email"
              type="email"
              label="E-mail"
              name="email"
              autoComplete="email"
              {...field}
            />}
          />
          {
            errors?.email && (
              <FormHelperText error>{errors.email?.message}</FormHelperText>
            )
          }
          <Controller
            name="postcode"
            control={control}
            render={({ field }) => <TextField
              fullWidth
              id="postcode"
              label="Postcode"
              name="postcode"
              {...field}
            />}
          />
          {seletedReader && <>
            <Box>
              <LoadingButton
                disabled={isLoading || !selectedTime}
                type='submit'
                variant='contained'
                color="primary"
                sx={{
                  marginTop: 4,
                  width: '100%',
                  padding: 2,
                }}>
                Collect Payment
              </LoadingButton>
            </Box>
          </>}
        </Stack>
      </form>
      {
        totalAmount && <CollectPaymentDialog open={openCollectPaymentDailog} totalAmount={totalAmount} onCancel={handleCancelPayment} />
      }
    </Box>
  )
}

export default Checkout;
